import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  BaseButton,
  Input,
  formatDateToWeekdayString,
  formatTimeRange,
  useT,
} from '@repo/common';

import RefundItemList from '../../components/MyPage/Refund/RefundItemList';
import RefundSummary from '../../components/MyPage/Refund/RefundSummary';
import ReasonSelect from '../../components/MyPage/Refund/ReasonSelect';

import { ReactComponent as ArrowBottomIcon } from '@repo/common/assets/icons/16px/icon_16px_arrow-bottom.svg';
import { ReactComponent as ArrowTopIcon } from '@repo/common/assets/icons/16px/icon_16px_arrow-top.svg';

import { getInvoiceDetail } from '../../api/payment/payment.api';
import { requestRefund } from '../../api/booking/booking.api';

const REFUND_REASONS = [
  { label: 'Choose a reason', labelKr: '사유를 선택해 주세요' },
  { label: 'My plans have changed', labelKr: '일정이 변경되었어요' },
  { label: 'I have a scheduling conflict', labelKr: '다른 일정과 겹쳐요' },
  { label: 'I booked this by mistake', labelKr: '실수로 예약했어요' },
  {
    label: 'I’m not satisfied with the workshop',
    labelKr: '워크숍이 마음에 들지 않아요',
  },
  { label: 'Other', labelKr: '기타' },
];

export default function RefundRequest() {
  const t = useT();
  const navigate = useNavigate();
  const location = useLocation();

  const { invoiceNumber } = useParams();
  const bookingIds = location.state?.bookingIds ?? [];

  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reason, setReason] = useState('Choose a reason');
  const [reasonError, setReasonError] = useState(false);
  const [reasonOpen, setReasonOpen] = useState(false);
  const reasonRef = useRef(null);
  const [otherText, setOtherText] = useState('');
  const [otherError, setOtherError] = useState(false);
  const isOther = reason === 'Other';
  const isReasonSelected = reason && reason !== 'Choose a reason';
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!reasonOpen) return;
    const onDown = (e) => {
      if (reasonRef.current && !reasonRef.current.contains(e.target)) {
        setReasonOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [reasonOpen]);

  useEffect(() => {
    const load = async () => {
      try {
        const invoice = await getInvoiceDetail(invoiceNumber);

        // 결제 수단·통화는 인보이스가 알려준다. 계좌이체(KRW) 예약을 "Card / $" 로 보여주면 안 된다
        const isBankTransfer =
          String(invoice.paymentProvider || '').toUpperCase() === 'BANK_TRANSFER';
        const paymentMethodLabel = isBankTransfer
          ? t('Bank transfer', '계좌이체')
          : t('Card', '카드');
        const invoiceCurrency =
          String(invoice.currency || '').toUpperCase() === 'KRW' ? 'KRW' : '$';

        const items = (invoice.bookingIds ?? [])
          .map((bookingId, i) => ({
            bookingId,
            serialNumber: invoice.bookingSerialNumbers?.[i],
            workshopName: invoice.workshopTitles?.[i],
            thumbnailUrl: invoice.thumbnailUrls?.[i] ?? null,
            dateLabel: `${formatDateToWeekdayString(invoice.workshopDates?.[i]?.[0])} ∙ ${formatTimeRange(
              invoice.startTimes?.[i]?.[0],
              invoice.endTimes?.[i]?.[0],
            )}`,
            paymentMethod: paymentMethodLabel,
            originalPrice: invoice.amounts?.[i],
            status: invoice.statuses?.[i],
          }))
          .filter((item) => item.status === 'CONFIRMED');

        const selectedItemTotal = items.reduce(
          (sum, i) => sum + Number(i.originalPrice ?? 0),
          0,
        );

        // 정책 구간(7일 전 전액 / 3~6일 전 50% 등)에 따른 수수료는 어드민이 승인 시 금액을 정한다.
        // 여기서는 요청 화면이므로 예상 금액을 전액으로 보여주고 문구로 안내한다.
        const cancellationFee = 0;

        setPreview({
          currency: invoiceCurrency,
          isBankTransfer,
          items,
          selectedItemTotal,
          cancellationFee,
          estimatedRefund: selectedItemTotal - cancellationFee,
        });
      } catch {
        toast.error(
          t('Could not load refund details.', '환불 정보를 불러오지 못했습니다.'),
        );
        navigate(-1);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [invoiceNumber]);

  if (!preview) return null;

  const currency = preview.currency ?? '$';

  const handleSubmit = async () => {
    const noReason = !isReasonSelected;
    const noOther = isOther && !otherText.trim();
    setReasonError(noReason);
    setOtherError(noOther);
    if (noReason || noOther) return;

    const finalReason = isOther ? `Other: ${otherText.trim()}` : reason;
    setSubmitting(true);
    try {
      await requestRefund(invoiceNumber, finalReason);
      navigate(`/mypage/${invoiceNumber}/refund/success`, { replace: true });
    } catch {
      toast.error(
        t(
          'Refund request failed. Please try again.',
          '환불 요청에 실패했습니다. 다시 시도해 주세요.',
        ),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className='flex flex-col items-center justify-center w-full p-10 pb-[120px] min-h-screen'>
      {/* 헤더 + 약관 안내 */}
      <div className='w-full max-w-[584px] flex flex-col'>
        <div className='flex flex-col items-start gap-2 pb-6'>
          <h1 className='typo-title1 text-text-primary'>
            {t('Request a refund', '환불 요청')}
          </h1>
          <p className='typo-b12 text-text-placeholder'>
            {t('I agree to the', '아래 내용의')}{' '}
            <Link
              to='/refund-policy'
              className='underline text-text-primary'
            >
              {t('Cancellation & Refund Details', '취소 및 환불 안내')}
            </Link>{' '}
            {t(
              'and request a refund for the selected items. The booking will be canceled once the refund is completed.',
              '에 동의하며 선택한 항목에 대한 환불을 요청합니다. 환불이 완료되면 예약이 취소됩니다.',
            )}
          </p>
        </div>

        {/* 선택 아이템 */}
        <div className='flex flex-col w-full gap-4 pb-[60px]'>
          <div className='flex flex-col w-full'>
            <h2 className='typo-b16Strong text-text-primary'>
              {t(
                'Please review the selected items',
                '선택한 항목을 확인해 주세요',
              )}
            </h2>
            <RefundItemList items={preview.items} currency={currency} />

            {/* 금액 요약 */}
            <RefundSummary
              selectedItemTotal={preview.selectedItemTotal}
              cancellationFee={preview.cancellationFee}
              estimatedRefund={preview.estimatedRefund}
              currency={currency}
            />
            <p className='typo-b12 text-text-placeholder -mt-2'>
              {t(
                'The final amount follows the workshop’s cancellation policy and is confirmed when your request is reviewed.',
                '최종 환불 금액은 워크숍의 취소 정책에 따라 검토 시 확정됩니다.',
              )}
              {preview.isBankTransfer &&
                ` ${t(
                  'This booking was paid by bank transfer, so the organizer will send the refund to your bank account.',
                  '이 예약은 계좌이체로 결제되어 주최측이 계좌로 환불해 드립니다.',
                )}`}
            </p>
          </div>

          {/* 사유 */}
          <div className='flex flex-col gap-2' ref={reasonRef}>
            <div className='relative'>
              <Input
                label={t('Reason for Refund*', '환불 사유*')}
                id='reason-input'
                placeholder={t('Choose a reason', '사유를 선택해 주세요')}
                value={
                  isReasonSelected
                    ? t(
                        reason,
                        REFUND_REASONS.find((item) => item.label === reason)
                          ?.labelKr ?? reason,
                      )
                    : ''
                }
                state={reasonError ? 'failed' : 'default'}
                message={
                  reasonError
                    ? t(
                        'Please select a reason for refund.',
                        '환불 사유를 선택해 주세요.',
                      )
                    : undefined
                }
                showStatusIcon={true}
                readOnly
                rightIcon={
                  reasonOpen ? (
                    <ArrowTopIcon className='text-icon-secondary' />
                  ) : (
                    <ArrowBottomIcon className='text-icon-secondary' />
                  )
                }
                onClick={() => setReasonOpen((o) => !o)}
                onRightIconClick={() => setReasonOpen((o) => !o)}
              />

              {reasonOpen && (
                <div className='absolute left-0 top-full z-20 mt-1 w-full'>
                  <ReasonSelect
                    selectedOption={t(
                      reason,
                      REFUND_REASONS.find((item) => item.label === reason)
                        ?.labelKr ?? reason,
                    )}
                    options={REFUND_REASONS.map((item) => ({
                      optionLabel: t(item.label, item.labelKr),
                    }))}
                    onSelect={(selectedLabel) => {
                      const matched = REFUND_REASONS.find(
                        (item) =>
                          t(item.label, item.labelKr) === selectedLabel,
                      );
                      const option = matched?.label ?? selectedLabel;
                      setReason(option);
                      setReasonError(false);
                      if (option !== 'Other') {
                        setOtherText('');
                        setOtherError(false);
                      }
                      setReasonOpen(false);
                    }}
                  />
                </div>
              )}
            </div>

            {isOther && (
              <Input
                as='textarea'
                label=''
                id='reason-other-input'
                placeholder={t(
                  'Tell us more about your reason',
                  '사유를 자세히 알려주세요',
                )}
                value={otherText}
                maxLength={200}
                state={otherError ? 'failed' : 'default'}
                message={
                  otherError
                    ? t('Please provide more details.', '내용을 입력해 주세요.')
                    : undefined
                }
                showStatusIcon={true}
                onChange={(e) => {
                  setOtherText(e.target.value);
                  if (e.target.value.trim()) setOtherError(false);
                }}
              />
            )}
          </div>
        </div>

        {/* 액션 */}
        <div className='flex flex-col items-center'>
          <BaseButton
            type='primary'
            size='xl'
            hideIcon
            width='100%'
            isDisabled={submitting}
            bgclass='bg-functional-failed03 border-functional-failed03'
            hoverclass='hover:bg-functional-failed03/90 hover:border-functional-failed03/90'
            activeclass='active:bg-functional-failed03/90 active:border-functional-failed03/90'
            onClick={handleSubmit}
          >
            {submitting
              ? t('Submitting…', '제출 중…')
              : t('Submit refund', '환불 요청하기')}
          </BaseButton>
          <BaseButton
            type='text_basic'
            size='xl'
            onClick={() => navigate(-1)}
            width='100%'
            hideIcon
            bgclass='border-none'
          >
            {t('cancel', '취소')}
          </BaseButton>
        </div>
      </div>
    </div>
  );
}
